import { useCallback, useMemo, useState } from 'react'
import { isMobile } from 'APP/Helpers/checkPlatform'
import { UseFormTrigger } from 'react-hook-form'
import { FORM_FIELDS } from '../constants'
import { AddDependentFormData } from '../types'

export type UseFormFieldVisibilityReturn = {
  handleDatePickerOpen: () => void
  handleDatePickerClose: () => void
  createHandleDatePickerFocus: (setFocusedField: (field: string | null) => void) => () => void
  createHandleDatePickerSelect: (
    setFocusedField: (field: string | null) => void,
    trigger: UseFormTrigger<AddDependentFormData>
  ) => () => void
  shouldHideChildFields: boolean
  shouldHideAdultFields: boolean
}

/**
 * Custom hook for managing form field visibility based on date picker modal state.
 *
 * Handles:
 * - Date picker modal open/close state
 * - Field visibility logic based on dependent type (child/adult) and modal state
 * - On mobile, hides fields when date picker is open to prevent UI overlap
 *
 * @param isChild - Whether the dependent is a child
 * @param isAdult - Whether the dependent is an adult
 * @returns Visibility state and handlers for date picker interactions
 */
export const useFormFieldVisibility = ({
  isChild,
  isAdult,
}: {
  isChild: boolean
  isAdult: boolean
}): UseFormFieldVisibilityReturn => {
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  const handleDatePickerClose = () => {
    setIsDatePickerOpen(false)
  }

  const handleDatePickerOpen = () => {
    setIsDatePickerOpen(true)
  }

  const createHandleDatePickerFocus = useCallback(
    (setFocusedField: (field: string | null) => void) => {
      return () => {
        setFocusedField(FORM_FIELDS.DATE_OF_BIRTH)
        handleDatePickerOpen()
      }
    },
    []
  )

  const createHandleDatePickerSelect = useCallback(
    (
      setFocusedField: (field: string | null) => void,
      trigger: UseFormTrigger<AddDependentFormData>
    ) => {
      return () => {
        setFocusedField(null)
        trigger(FORM_FIELDS.DATE_OF_BIRTH)
      }
    },
    []
  )

  const shouldHideChildFields = useMemo(() => {
    if (isMobile()) {
      return !isChild || isDatePickerOpen
    }
    return !isChild
  }, [isChild, isDatePickerOpen])

  const shouldHideAdultFields = useMemo(() => {
    if (isMobile()) {
      return !isAdult || isDatePickerOpen
    }
    return !isAdult
  }, [isAdult, isDatePickerOpen])

  return {
    handleDatePickerOpen,
    handleDatePickerClose,
    createHandleDatePickerFocus,
    createHandleDatePickerSelect,
    shouldHideAdultFields,
    shouldHideChildFields,
  }
}
